import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import { CheckListItem } from '@/app/components/CheckListItem'
import MCPChatMessages from '@/app/components/MCPChatMessages'
import { Media } from '@/app/components/Media'
import PortableText from '@/app/components/PortableText'
import Video, { hasVideo } from '@/app/components/Video'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import {
  cn,
  ptFontNormalHeaders,
  ptWhiteTextHeaders,
  ptWhiteTextParagraphs,
} from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'
import { HeroSection as HeroSectionProps } from '@/sanity.types'

type HeroProps = {
  block: HeroSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

export default function HeroSection({ block, pageId, pageType }: HeroProps) {
  const heroTheme = stegaClean(block.heroTheme)
  const isDark = heroTheme === 'dark'

  const showVideo = hasVideo(block.video)
  const mediaType = stegaClean(block.mediaType)
  const layoutTweaks = stegaClean(block.layoutTweaks)
  const isMCPPage = layoutTweaks === 'mcp'
  const showImage = isMCPPage ? true : mediaType === 'image' && block.image
  const layout = stegaClean(block.layout) || 'default'
  const isStacked = layout === 'stacked'
  const hasMedia = mediaType === 'image' || mediaType === 'video'
  const imagePosition = isMCPPage ? 'right' : stegaClean(block.imagePosition) || 'right'

  const contentElement = block?.content?.length && (
    <PortableText
      className={cn(
        'flex flex-col items-center justify-center text-center mx-auto',
        isStacked ? !hasMedia && 'lg:max-w-[848px]' : 'lg:max-w-[848px]',
        !isStacked && mediaType === 'image' && 'md:text-left md:items-start',
        isDark && ptWhiteTextParagraphs,
        isDark && ptWhiteTextHeaders,
        ptFontNormalHeaders,
        isMCPPage && 'mcp-ai-icon xl:text-left xl:items-start',
      )}
      tracking={{ location: TRACKING_LOCATIONS.HERO }}
      buttonClassName={buttonSizeClasses({ lg: 'md' })}
      value={block.content as PortableTextBlock[]}
      documentId={pageId}
      documentType={pageType}
      parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
      componentOverrides={{
        listItem: {
          checked: ({ children }) => <CheckListItem size="l">{children}</CheckListItem>,
        },
        block: {
          ...(isStacked && {
            'h3': ({ children }) => {
              const id = generateElementId(children)
              return (
                <h1
                  id={id}
                  data-heading-id={id}
                  data-anchor-expose="true"
                  className={cn(
                    'size-header-h4 !font-medium lg:size-header-h3',
                    isDark ? 'text-white-default' : 'text-white-950',
                  )}
                >
                  {children}
                </h1>
              )
            },
            'body-lg': ({ children }) => {
              const id = generateElementId(children)
              return (
                <h2
                  id={id}
                  data-heading-id={id}
                  data-anchor-expose="true"
                  className={cn('size-body-lg', isDark ? 'text-white-default' : 'text-white-950')}
                >
                  {children}
                </h2>
              )
            },
          }),
          ...(isMCPPage &&
            !isStacked && {
              h1: ({ children }) => {
                const id = generateElementId(children)
                return (
                  <h1
                    id={id}
                    data-heading-id={id}
                    data-anchor-expose="true"
                    className={cn(
                      'size-header-h3 !font-medium lg:size-header-h1 lg:!font-normal',
                      isDark ? 'text-white-default' : 'text-white-950',
                    )}
                    style={{ lineHeight: '1.2 !important' } as React.CSSProperties}
                  >
                    {children}
                  </h1>
                )
              },
            }),
          'body-xl': ({ children }) => {
            const id = generateElementId(children)
            return (
              <h2
                id={id}
                data-heading-id={id}
                data-anchor-expose="true"
                className={cn(
                  'size-body-md lg:size-body-xl',
                  isDark ? 'text-white-default' : 'text-white-950',
                )}
              >
                {children}
              </h2>
            )
          },
        },
      }}
    />
  )

  return (
    <>
      <div
        className={cn(
          isDark ? 'hero-container-dark py-6xl lg:py-9xl' : 'hero-container my-6xl lg:my-9xl',
          'relative',
          isDark
            ? 'bg-green-700 overflow-visible before:absolute before:inset-x-0 before:top-0 before:-translate-y-full before:h-[100vh] before:bg-green-700'
            : 'overflow-hidden',
        )}
      >
        <div className="relative z-10">
          {showImage ? (
            isStacked ? (
              <div className="mx-auto flex flex-col items-center gap-7xl md:max-w-[1144px]">
                <div className="flex-1 w-full">{contentElement}</div>
                <div className="flex-1 w-full max-w-[592px]">
                  <Media image={block.image} sizes="(max-width: 768px) 100vw, 592px" />
                  {block.imageCaption && (
                    <p
                      className={cn(
                        'size-body-lg text-center mt-lg',
                        isDark ? 'text-white-default' : 'text-white-950',
                      )}
                    >
                      {stegaClean(block.imageCaption)}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  'mx-auto flex max-w-[528px] flex-col gap-4xl md:max-w-[1144px] md:items-center md:gap-6xl',
                  isMCPPage ? 'lg:gap-9xl' : 'lg:gap-10xl',
                  isMCPPage
                    ? imagePosition === 'left'
                      ? 'xl:flex-row-reverse'
                      : 'xl:flex-row'
                    : imagePosition === 'left'
                      ? 'md:flex-row-reverse'
                      : 'md:flex-row',
                )}
              >
                <div className="flex-1">{contentElement}</div>
                <div className={cn('flex-1', isMCPPage && 'md:w-full')}>
                  {isMCPPage ? (
                    <div className="flex justify-center mb-6xl lg:mb-0">
                      <div
                        className="bg-green-500 rounded-md w-full mx-auto max-w-[548px] py-4xl px-2xl flex justify-center"
                        style={{
                          backgroundImage: 'url(/images_pwf/ai_hero_bg_80.png)',
                          backgroundRepeat: 'repeat',
                          backgroundPosition: 'right center',
                          backgroundSize: '178px 410px',
                        }}
                      >
                        <MCPChatMessages className="max-w-[360px]" />
                      </div>
                    </div>
                  ) : (
                    <Media
                      image={block.image}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 420px"
                    />
                  )}
                </div>
              </div>
            )
          ) : (
            contentElement
          )}
          {showVideo && (
            <div className="mt-3xl md:mt-5xl lg:mt-7xl mx-auto max-w-[1120px]">
              <Video video={block.video} className="rounded-md lg:rounded-lg overflow-hidden" />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
